// Model Configuration System
// Defines parameters and capabilities for each AI model

import { MODELS } from './app';

export type ParameterType = 'aspectRatio' | 'duration' | 'temperature' | 'model';

export interface ParameterOption {
  value: string;
  label: string;
}

export interface ModelParameter {
  id: ParameterType;
  label: string;
  type: 'select' | 'slider';
  options?: ParameterOption[];
  min?: number;
  max?: number;
  step?: number;
  defaultValue: string | number;
}

export interface ModelConfig {
  id: string;
  name: string;
  category: 'text' | 'image' | 'video';
  parameters: ModelParameter[];
}

// Aspect ratio options for image generation
const IMAGE_ASPECT_RATIOS: ParameterOption[] = [
  { value: '9:16', label: '9:16 (Portrait)' },
  { value: '1:1', label: '1:1 (Square)' },
  { value: '4:5', label: '4:5' },
  { value: '16:9', label: '16:9 (Landscape)' },
  { value: '4:3', label: '4:3' },
  { value: '3:4', label: '3:4' },
  { value: '3:2', label: '3:2' },
  { value: '2:3', label: '2:3' },
  { value: '5:4', label: '5:4' },
];

// Aspect ratio options for video generation
const VIDEO_ASPECT_RATIOS: ParameterOption[] = [
  { value: '9:16', label: '9:16 (Portrait)' },
  { value: '16:9', label: '16:9 (Landscape)' },
];

// Video duration options
const VIDEO_DURATIONS: ParameterOption[] = [
  { value: '4', label: '4 seconds' },
  { value: '6', label: '6 seconds' },
  { value: '8', label: '8 seconds' },
];

// Video model options
const VIDEO_MODEL_OPTIONS: ParameterOption[] = [
  { value: 'veo-3.1-fast-generate-preview', label: 'VEO 3.1 Fast' },
  { value: 'veo-3.1-generate-preview', label: 'VEO 3.1' },
  { value: 'veo-3.1-lite-generate-preview', label: 'VEO 3.1 Lite' },
  { value: 'gemini-omni-1.1-flash', label: 'Gemini Omni 1.1 Flash' },
];

// Model Configurations
export const MODEL_CONFIGS: Record<string, ModelConfig> = {
  // Google Image Model (Nano Banana / Gemini 2.5 Flash Image)
  [MODELS.GOOGLE_IMAGE]: {
    id: MODELS.GOOGLE_IMAGE,
    name: 'Nano Banana (Gemini 2.5 Flash Image)',
    category: 'image',
    parameters: [
      {
        id: 'aspectRatio',
        label: 'Aspect Ratio',
        type: 'select',
        options: IMAGE_ASPECT_RATIOS,
        defaultValue: '9:16',
      },
    ],
  },

  // Alias for custom nodes using 'nano-banana'
  'nano-banana': {
    id: 'nano-banana',
    name: 'Nano Banana (Gemini 2.5 Flash Image) 🍌',
    category: 'image',
    parameters: [
      {
        id: 'aspectRatio',
        label: 'Aspect Ratio',
        type: 'select',
        options: IMAGE_ASPECT_RATIOS,
        defaultValue: '9:16',
      },
    ],
  },

  // Nano Banana 2 (speed-optimized, cheaper)
  'gemini-3.1-flash-image': {
    id: 'gemini-3.1-flash-image',
    name: 'Nano Banana 2 (Gemini 3.1 Flash Image) 🍌',
    category: 'image',
    parameters: [
      {
        id: 'aspectRatio',
        label: 'Aspect Ratio',
        type: 'select',
        options: IMAGE_ASPECT_RATIOS,
        defaultValue: '9:16',
      },
    ],
  },

  // Nano Banana 2 Lite (fastest + cheapest; 1K only)
  'gemini-3.1-flash-lite-image': {
    id: 'gemini-3.1-flash-lite-image',
    name: 'Nano Banana 2 Lite (Gemini 3.1 Flash Lite Image) 🍌',
    category: 'image',
    parameters: [
      {
        id: 'aspectRatio',
        label: 'Aspect Ratio',
        type: 'select',
        options: IMAGE_ASPECT_RATIOS,
        defaultValue: '9:16',
      },
    ],
  },

  // Google Video Models (VEO 3.1)
  'veo-3.1-fast-generate-preview': {
    id: 'veo-3.1-fast-generate-preview',
    name: 'VEO 3.1 Fast',
    category: 'video',
    parameters: [
      {
        id: 'model',
        label: 'Video Model',
        type: 'select',
        options: VIDEO_MODEL_OPTIONS,
        defaultValue: 'veo-3.1-fast-generate-preview',
      },
      {
        id: 'duration',
        label: 'Duration',
        type: 'select',
        options: VIDEO_DURATIONS,
        defaultValue: '8',
      },
      {
        id: 'aspectRatio',
        label: 'Aspect Ratio',
        type: 'select',
        options: VIDEO_ASPECT_RATIOS,
        defaultValue: '9:16',
      },
    ],
  },

  // Gemini Omni 1.1 Flash (Google direct) — 720p, priced like VEO 3.1 Fast.
  'gemini-omni-1.1-flash': {
    id: 'gemini-omni-1.1-flash',
    name: 'Gemini Omni 1.1 Flash',
    category: 'video',
    parameters: [
      {
        id: 'model',
        label: 'Video Model',
        type: 'select',
        options: VIDEO_MODEL_OPTIONS,
        defaultValue: 'gemini-omni-1.1-flash',
      },
      {
        id: 'duration',
        label: 'Duration',
        type: 'select',
        options: VIDEO_DURATIONS,
        defaultValue: '5',
      },
      {
        id: 'aspectRatio',
        label: 'Aspect Ratio',
        type: 'select',
        options: VIDEO_ASPECT_RATIOS,
        defaultValue: '16:9',
      },
    ],
  },

  'veo-3.1-generate-preview': {
    id: 'veo-3.1-generate-preview',
    name: 'VEO 3.1',
    category: 'video',
    parameters: [
      {
        id: 'model',
        label: 'Video Model',
        type: 'select',
        options: VIDEO_MODEL_OPTIONS,
        defaultValue: 'veo-3.1-generate-preview',
      },
      {
        id: 'duration',
        label: 'Duration',
        type: 'select',
        options: VIDEO_DURATIONS,
        defaultValue: '8',
      },
      {
        id: 'aspectRatio',
        label: 'Aspect Ratio',
        type: 'select',
        options: VIDEO_ASPECT_RATIOS,
        defaultValue: '9:16',
      },
    ],
  },

  'veo-3.1-lite-generate-preview': {
    id: 'veo-3.1-lite-generate-preview',
    name: 'VEO 3.1 Lite',
    category: 'video',
    parameters: [
      {
        id: 'model',
        label: 'Video Model',
        type: 'select',
        options: VIDEO_MODEL_OPTIONS,
        defaultValue: 'veo-3.1-lite-generate-preview',
      },
      {
        id: 'duration',
        label: 'Duration',
        type: 'select',
        options: VIDEO_DURATIONS,
        defaultValue: '8',
      },
      {
        id: 'aspectRatio',
        label: 'Aspect Ratio',
        type: 'select',
        options: VIDEO_ASPECT_RATIOS,
        defaultValue: '9:16',
      },
    ],
  },

  // Alias for custom nodes
  'veo-3.1-fast': {
    id: 'veo-3.1-fast',
    name: 'VEO 3.1 Fast',
    category: 'video',
    parameters: [
      {
        id: 'model',
        label: 'Video Model',
        type: 'select',
        options: VIDEO_MODEL_OPTIONS,
        defaultValue: 'veo-3.1-fast-generate-preview',
      },
      {
        id: 'duration',
        label: 'Duration',
        type: 'select',
        options: VIDEO_DURATIONS,
        defaultValue: '8',
      },
      {
        id: 'aspectRatio',
        label: 'Aspect Ratio',
        type: 'select',
        options: VIDEO_ASPECT_RATIOS,
        defaultValue: '9:16',
      },
    ],
  },

  // OpenAI Model (text and vision, no parameters)
  [MODELS.OPENAI]: {
    id: MODELS.OPENAI,
    name: 'GPT-5 Nano',
    category: 'text',
    parameters: [],
  },

  // Alias for custom nodes using 'gpt-4o-mini'
  'gpt-4o-mini': {
    id: 'gpt-4o-mini',
    name: 'GPT-4o Mini',
    category: 'text',
    parameters: [],
  },

  // Google Text Model (no parameters)
  [MODELS.GOOGLE_TEXT]: {
    id: MODELS.GOOGLE_TEXT,
    name: 'Gemini 2.5 Flash',
    category: 'text',
    parameters: [],
  },
};

/**
 * Get model configuration by model ID
 */
export function getModelConfig(modelId: string): ModelConfig | undefined {
  return MODEL_CONFIGS[modelId];
}

/**
 * Get default parameter values for a model
 */
export function getDefaultParameters(modelId: string): Record<string, string | number> {
  const config = getModelConfig(modelId);
  if (!config) return {};

  const defaults: Record<string, string | number> = {};
  config.parameters.forEach(param => {
    defaults[param.id] = param.defaultValue;
  });
  return defaults;
}

/**
 * Check if a model has a specific parameter
 */
export function hasParameter(modelId: string, parameterId: ParameterType): boolean {
  const config = getModelConfig(modelId);
  return config?.parameters.some(p => p.id === parameterId) ?? false;
}

/**
 * Get parameter configuration for a specific model and parameter ID
 */
export function getParameterConfig(modelId: string, parameterId: ParameterType): ModelParameter | undefined {
  const config = getModelConfig(modelId);
  return config?.parameters.find(p => p.id === parameterId);
}

// ============================================================================
// Model Registry for UI (Model Selectors)
// ============================================================================

export interface ModelRegistryEntry {
  id: string;
  name: string;
  provider: 'Google' | 'OpenAI' | 'Together' | 'FalAI' | 'Anthropic' | 'NVIDIA' | 'MiniMax' | 'Qwen' | 'Kimi' | 'xAI';
  mediaTypes: ('text' | 'image' | 'video')[];
}

/**
 * Centralized model registry for UI components (dropdowns, selectors, etc.)
 * Maps model IDs to display information
 */
export const MODEL_REGISTRY: Record<string, ModelRegistryEntry> = {
  // Google Image Models
  [MODELS.GOOGLE_IMAGE]: {
    id: MODELS.GOOGLE_IMAGE,
    name: 'Gemini 2.5 (Nano Banana)',
    provider: 'Google',
    mediaTypes: ['image'],
  },
  'gemini-nano-banana': {
    id: 'gemini-nano-banana',
    name: 'Gemini 2.5 Flash Image 🍌',
    provider: 'Google',
    mediaTypes: ['image'],
  },
  'gemini-3-pro-image-preview': {
    id: 'gemini-3-pro-image-preview',
    name: 'Banana Pro 🍌',
    provider: 'Google',
    mediaTypes: ['image'],
  },

  // Google Video Models
  'veo-3.1-fast-generate-preview': {
    id: 'veo-3.1-fast-generate-preview',
    name: 'VEO 3.1 Fast',
    provider: 'Google',
    mediaTypes: ['video'],
  },
  'gemini-omni-1.1-flash': {
    id: 'gemini-omni-1.1-flash',
    name: 'Gemini Omni 1.1 Flash',
    provider: 'Google',
    mediaTypes: ['video'],
  },
  // Retired from the dropdown 2026-09-01, shut down by Google 2026-09-30. Kept
  // so a saved flow pinning the id still resolves a display name instead of
  // rendering the raw id.
  'gemini-omni-flash-preview': {
    id: 'gemini-omni-flash-preview',
    name: 'Gemini Omni Flash',
    provider: 'Google',
    mediaTypes: ['video'],
  },
  'veo-3.1-generate-preview': {
    id: 'veo-3.1-generate-preview',
    name: 'VEO 3.1',
    provider: 'Google',
    mediaTypes: ['video'],
  },
  'veo-3.1-lite-generate-preview': {
    id: 'veo-3.1-lite-generate-preview',
    name: 'VEO 3.1 Lite',
    provider: 'Google',
    mediaTypes: ['video'],
  },
  'veo-3-fast': {
    id: 'veo-3-fast',
    name: 'VEO 3 Fast',
    provider: 'Google',
    mediaTypes: ['video'],
  },
  'veo-3-pro': {
    id: 'veo-3-pro',
    name: 'VEO 3 Pro',
    provider: 'Google',
    mediaTypes: ['video'],
  },

  // Google Text Models
  [MODELS.GOOGLE_TEXT]: {
    id: MODELS.GOOGLE_TEXT,
    name: 'Gemini 2.5 Flash',
    provider: 'Google',
    mediaTypes: ['text'],
  },

  // OpenAI Models — GPT-5.x series
  [MODELS.OPENAI]: {
    id: MODELS.OPENAI,
    name: 'GPT-5 Nano',
    provider: 'OpenAI',
    mediaTypes: ['text', 'image'],
  },
  [MODELS.OPENAI_MINI]: {
    id: MODELS.OPENAI_MINI,
    name: 'GPT-5 Mini',
    provider: 'OpenAI',
    mediaTypes: ['text', 'image'],
  },
  [MODELS.OPENAI_STANDARD]: {
    id: MODELS.OPENAI_STANDARD,
    name: 'GPT-5',
    provider: 'OpenAI',
    mediaTypes: ['text', 'image'],
  },
  [MODELS.OPENAI_PRO]: {
    id: MODELS.OPENAI_PRO,
    name: 'GPT-5.2',
    provider: 'OpenAI',
    mediaTypes: ['text', 'image'],
  },
  // OpenAI Models — GPT-5.4 series (March 2026)
  [MODELS.OPENAI_5_4_NANO]: {
    id: MODELS.OPENAI_5_4_NANO,
    name: 'GPT-5.4 Nano',
    provider: 'OpenAI',
    mediaTypes: ['text', 'image'],
  },
  [MODELS.OPENAI_5_4]: {
    id: MODELS.OPENAI_5_4,
    name: 'GPT-5.4',
    provider: 'OpenAI',
    mediaTypes: ['text', 'image'],
  },
  [MODELS.OPENAI_5_4_PRO]: {
    id: MODELS.OPENAI_5_4_PRO,
    name: 'GPT-5.4 Pro',
    provider: 'OpenAI',
    mediaTypes: ['text', 'image'],
  },
  // OpenAI Models — GPT-5.5 series (April 2026)
  [MODELS.OPENAI_5_5]: {
    id: MODELS.OPENAI_5_5,
    name: 'GPT-5.5',
    provider: 'OpenAI',
    mediaTypes: ['text', 'image'],
  },
  [MODELS.OPENAI_5_5_PRO]: {
    id: MODELS.OPENAI_5_5_PRO,
    name: 'GPT-5.5 Pro',
    provider: 'OpenAI',
    mediaTypes: ['text', 'image'],
  },
  // OpenAI Models — GPT-5.6 series (July 2026), current generation.
  // Every OpenAI chat model accepts image input; there is no text-only tier.
  [MODELS.OPENAI_5_6_SOL]: {
    id: MODELS.OPENAI_5_6_SOL,
    name: 'GPT-5.6 Sol',
    provider: 'OpenAI',
    mediaTypes: ['text', 'image'],
  },
  [MODELS.OPENAI_5_6_TERRA]: {
    id: MODELS.OPENAI_5_6_TERRA,
    name: 'GPT-5.6 Terra',
    provider: 'OpenAI',
    mediaTypes: ['text', 'image'],
  },
  [MODELS.OPENAI_5_6_LUNA]: {
    id: MODELS.OPENAI_5_6_LUNA,
    name: 'GPT-5.6 Luna',
    provider: 'OpenAI',
    mediaTypes: ['text', 'image'],
  },
  [MODELS.OPENAI_5_4_MINI]: {
    id: MODELS.OPENAI_5_4_MINI,
    name: 'GPT-5.4 Mini',
    provider: 'OpenAI',
    mediaTypes: ['text', 'image'],
  },

  // OpenAI Image Models
  'gpt-image-2': {
    id: 'gpt-image-2',
    name: 'GPT Image 2',
    provider: 'OpenAI',
    mediaTypes: ['image'],
  },
  'gpt-image-1.5': {
    id: 'gpt-image-1.5',
    name: 'GPT Image 1.5',
    provider: 'OpenAI',
    mediaTypes: ['image'],
  },
  'gpt-image-1-mini': {
    id: 'gpt-image-1-mini',
    name: 'GPT Image 1 Mini',
    provider: 'OpenAI',
    mediaTypes: ['image'],
  },

  // fal.ai FLUX Image Models (Black Forest Labs)
  'flux-2-max': {
    id: 'flux-2-max',
    name: 'FLUX 2 Max',
    provider: 'FalAI',
    mediaTypes: ['image'],
  },
  'flux-2-pro': {
    id: 'flux-2-pro',
    name: 'FLUX 2 Pro',
    provider: 'FalAI',
    mediaTypes: ['image'],
  },
  'flux-2-turbo': {
    id: 'flux-2-turbo',
    name: 'FLUX 2 Turbo',
    provider: 'FalAI',
    mediaTypes: ['image'],
  },
  'flux-2-flash': {
    id: 'flux-2-flash',
    name: 'FLUX 2 Flash',
    provider: 'FalAI',
    mediaTypes: ['image'],
  },
  'flux-pro-fill': {
    id: 'flux-pro-fill',
    name: 'FLUX Pro Fill',
    provider: 'FalAI',
    mediaTypes: ['image'],
  },

  // fal.ai Image Models
  'z-image-turbo': {
    id: 'z-image-turbo',
    name: 'Z-Image Turbo',
    provider: 'FalAI',
    mediaTypes: ['image'],
  },
  'qwen-image': {
    id: 'qwen-image',
    name: 'Qwen Image',
    provider: 'FalAI',
    mediaTypes: ['image'],
  },
  'qwen-image-edit': {
    id: 'qwen-image-edit',
    name: 'Qwen Image Edit',
    provider: 'FalAI',
    mediaTypes: ['image'],
  },
  'seedream-4.5': {
    id: 'seedream-4.5',
    name: 'Seedream 4.5',
    provider: 'FalAI',
    mediaTypes: ['image'],
  },
  'recraft-v3': {
    id: 'recraft-v3',
    name: 'Recraft V3',
    provider: 'FalAI',
    mediaTypes: ['image'],
  },
  'recraft-v4': {
    id: 'recraft-v4',
    name: 'Recraft V4',
    provider: 'FalAI',
    mediaTypes: ['image'],
  },
  'recraft-v4-pro': {
    id: 'recraft-v4-pro',
    name: 'Recraft V4 Pro',
    provider: 'FalAI',
    mediaTypes: ['image'],
  },
  'qwen-image-2': {
    id: 'qwen-image-2',
    name: 'Qwen Image 2',
    provider: 'FalAI',
    mediaTypes: ['image'],
  },
  'qwen-image-2-pro': {
    id: 'qwen-image-2-pro',
    name: 'Qwen Image 2 Pro',
    provider: 'FalAI',
    mediaTypes: ['image'],
  },
  'seedream-5-lite': {
    id: 'seedream-5-lite',
    name: 'Seedream 5 Lite',
    provider: 'FalAI',
    mediaTypes: ['image'],
  },
  'glm-image': {
    id: 'glm-image',
    name: 'GLM Image',
    provider: 'FalAI',
    mediaTypes: ['image'],
  },
  'kling-image-o3': {
    id: 'kling-image-o3',
    name: 'Kling Image',
    provider: 'FalAI',
    mediaTypes: ['image'],
  },
  'grok-image': {
    id: 'grok-image',
    name: 'Grok Image',
    provider: 'xAI',
    mediaTypes: ['image'],
  },
  'grok-image-pro': {
    id: 'grok-image-pro',
    name: 'Grok Image Pro',
    provider: 'xAI',
    mediaTypes: ['image'],
  },
  'grok-imagine-image-quality': {
    id: 'grok-imagine-image-quality',
    name: 'Grok Image Quality',
    provider: 'xAI',
    mediaTypes: ['image'],
  },
  'grok-imagine-image-2.0': {
    id: 'grok-imagine-image-2.0',
    name: 'Grok Image 2.0',
    provider: 'xAI',
    mediaTypes: ['image'],
  },
  'grok-imagine-video-1.5-preview': {
    id: 'grok-imagine-video-1.5-preview',
    name: 'Grok Video 1.5',
    provider: 'xAI',
    mediaTypes: ['video'],
  },
  // xAI Grok text models. All are multimodal (xAI's /v1/models reports a
  // prompt_image_token_price for each), so they accept image inputs too.
  'grok-4.5': {
    id: 'grok-4.5',
    name: 'Grok 4.5',
    provider: 'xAI',
    mediaTypes: ['text', 'image'],
  },
  'grok-4.3': {
    id: 'grok-4.3',
    name: 'Grok 4.3',
    provider: 'xAI',
    mediaTypes: ['text', 'image'],
  },
  'grok-build-0.1': {
    id: 'grok-build-0.1',
    name: 'Grok Build 0.1 (Code)',
    provider: 'xAI',
    mediaTypes: ['text', 'image'],
  },
  'ltx-2.3': {
    id: 'ltx-2.3',
    name: 'LTX-2.3 Pro',
    provider: 'FalAI',
    mediaTypes: ['video'],
  },
  'ltx-2.3-fast': {
    id: 'ltx-2.3-fast',
    name: 'LTX-2.3 Fast',
    provider: 'FalAI',
    mediaTypes: ['video'],
  },
  'hailuo-2.3': {
    id: 'hailuo-2.3',
    name: 'MiniMax Hailuo 2.3',
    provider: 'FalAI',
    mediaTypes: ['video'],
  },
  'minimax-h3': {
    id: 'minimax-h3',
    name: 'MiniMax H3',
    provider: 'FalAI',
    mediaTypes: ['video'],
  },
  'pixverse-6': {
    id: 'pixverse-6',
    name: 'PixVerse v6',
    provider: 'FalAI',
    mediaTypes: ['video'],
  },
  'seedance-2.5': {
    id: 'seedance-2.5',
    name: 'Seedance 2.5',
    provider: 'FalAI',
    mediaTypes: ['video'],
  },
  'seedance-2': {
    id: 'seedance-2',
    name: 'Seedance 2 Pro',
    provider: 'FalAI',
    mediaTypes: ['video'],
  },
  'seedance-2-fast': {
    id: 'seedance-2-fast',
    name: 'Seedance 2 Fast',
    provider: 'FalAI',
    mediaTypes: ['video'],
  },
  'seedance-2-mini': {
    id: 'seedance-2-mini',
    name: 'Seedance 2 Mini',
    provider: 'FalAI',
    mediaTypes: ['video'],
  },

  // Moonshot AI (Kimi) Models via Ollama Cloud
  'kimi-k2.6': {
    id: 'kimi-k2.6',
    name: 'Kimi K2.6 (Thinking, Vision)',
    provider: 'Kimi',
    mediaTypes: ['text', 'image'],
  },
  // Retired by Ollama on 2026-07-31 (https://ollama.com/library/kimi-k2.5).
  // Kept as a registry entry so saved flows that pin it still render a name and
  // a price; the server aliases it to kimi-k2.6:cloud. Removed from the dropdown.
  'kimi-k2.5': {
    id: 'kimi-k2.5',
    name: 'Kimi K2.5 (retired → K2.6)',
    provider: 'Kimi',
    mediaTypes: ['text', 'image'],
  },
  // Legacy alias — kept so old saved flows still resolve. Maps to kimi-k2.6 on the server.
  'kimi-k2-instruct': {
    id: 'kimi-k2-instruct',
    name: 'Kimi K2 Instruct (legacy)',
    provider: 'Kimi',
    mediaTypes: ['text', 'image'],
  },

  // Alibaba Qwen Models via Ollama Cloud
  'qwen-3.5': {
    id: 'qwen-3.5',
    name: 'Qwen 3.5 (Vision, 256K)',
    provider: 'Qwen',
    mediaTypes: ['text', 'image'],
  },
  // Qwen 3.5 397B — larger tier of the same vision-capable family.
  'qwen-3.5-397b': {
    id: 'qwen-3.5-397b',
    name: 'Qwen 3.5 397B (Vision, 256K)',
    provider: 'Qwen',
    mediaTypes: ['text', 'image'],
  },
  // Kept ONLY so saved flows that pin them still resolve a registry entry (the
  // settings footer crashes on an undefined model). Both map to qwen3.5:cloud:
  // 'qwen3-vl:235b-cloud' and 'qwen3-coder-next:cloud' are not real Ollama
  // Cloud tags, so these never actually ran what their names claim. Removed
  // from the node dropdown.
  'qwen-3-vl-235b': {
    id: 'qwen-3-vl-235b',
    name: 'Qwen 3-VL 235B (legacy → Qwen 3.5)',
    provider: 'Qwen',
    mediaTypes: ['text', 'image'],
  },
  'qwen-3-coder-next': {
    id: 'qwen-3-coder-next',
    name: 'Qwen 3 Coder Next (legacy → Qwen 3.5)',
    provider: 'Qwen',
    mediaTypes: ['text'],
  },
  // Legacy aliases — kept so old saved flows still resolve.
  'qwen-3.5-122b': {
    id: 'qwen-3.5-122b',
    name: 'Qwen 3.5 122B (legacy)',
    provider: 'Qwen',
    mediaTypes: ['text'],
  },
  'qwen-3-next-80b': {
    id: 'qwen-3-next-80b',
    name: 'Qwen 3 Next 80B (legacy)',
    provider: 'Qwen',
    mediaTypes: ['text'],
  },
  'qwen-2.5-7b': {
    id: 'qwen-2.5-7b',
    name: 'Qwen 2.5 7B (legacy)',
    provider: 'Qwen',
    mediaTypes: ['text'],
  },

  // MiniMax Models via Ollama Cloud
  'minimax-m2.7': {
    id: 'minimax-m2.7',
    name: 'MiniMax M2.7 (Agents)',
    provider: 'MiniMax',
    mediaTypes: ['text'],
  },
  // Retired by Ollama on 2026-07-31 (https://ollama.com/library/minimax-m2.5).
  // Same treatment as m2.1 below: kept for saved flows, aliased to m2.7.
  'minimax-m2.5': {
    id: 'minimax-m2.5',
    name: 'MiniMax M2.5 (retired → M2.7)',
    provider: 'MiniMax',
    mediaTypes: ['text'],
  },
  // Retired by Ollama on 2026-07-15. Kept as a registry entry so saved flows
  // that pin it still render; the server aliases it to m2.7. Removed from the
  // node dropdown.
  'minimax-m2.1': {
    id: 'minimax-m2.1',
    name: 'MiniMax M2.1 (retired → M2.7)',
    provider: 'MiniMax',
    mediaTypes: ['text'],
  },
  // Retired by Ollama on 2026-06-16 (https://ollama.com/library/minimax-m2).
  // Never appeared in a node dropdown — reachable only by headless/REST callers
  // writing the id by hand. Aliased to m2.7 so those callers keep working.
  'minimax-m2': {
    id: 'minimax-m2',
    name: 'MiniMax M2 (retired → M2.7)',
    provider: 'MiniMax',
    mediaTypes: ['text'],
  },

  // Anthropic Claude Models
  'claude-haiku-3-5': {
    id: 'claude-haiku-3-5',
    name: 'Claude Haiku 3.5',
    provider: 'Anthropic',
    mediaTypes: ['text'],
  },
  'claude-sonnet-4': {
    id: 'claude-sonnet-4',
    name: 'Claude Sonnet 4',
    provider: 'Anthropic',
    mediaTypes: ['text'],
  },
  'claude-sonnet-4-5': {
    id: 'claude-sonnet-4-5',
    name: 'Claude Sonnet 4.5',
    provider: 'Anthropic',
    // Corrected: every Claude 4.x model accepts image input. This was the only
    // Anthropic entry marked text-only, which made the Claude node look
    // "mixed" (image handle shown, one listed model claiming not to take one).
    mediaTypes: ['text', 'image'],
  },
  // Claude 4.x current generation (available March 2026)
  'claude-haiku-4-5': {
    id: 'claude-haiku-4-5',
    name: 'Claude Haiku 4.5',
    provider: 'Anthropic',
    mediaTypes: ['text', 'image'],
  },
  'claude-sonnet-4-6': {
    id: 'claude-sonnet-4-6',
    name: 'Claude Sonnet 4.6',
    provider: 'Anthropic',
    mediaTypes: ['text', 'image'],
  },
  'claude-opus-4-6': {
    id: 'claude-opus-4-6',
    name: 'Claude Opus 4.6',
    provider: 'Anthropic',
    mediaTypes: ['text', 'image'],
  },
  // Opus 4.7 (April 2026) — now marked Legacy by Anthropic but still available
  'claude-opus-4-7': {
    id: 'claude-opus-4-7',
    name: 'Claude Opus 4.7 (Legacy)',
    provider: 'Anthropic',
    mediaTypes: ['text', 'image'],
  },
  // Opus 4.8 (May 2026) — previous flagship Opus tier
  'claude-opus-4-8': {
    id: 'claude-opus-4-8',
    name: 'Claude Opus 4.8',
    provider: 'Anthropic',
    mediaTypes: ['text', 'image'],
  },
  // Claude Opus 5 — current Opus tier. Same $5/$25 per MTok as Opus 4.8, so it
  // slots into the existing 15 cr/generation pricing with no new math.
  'claude-opus-5': {
    id: 'claude-opus-5',
    name: 'Claude Opus 5',
    provider: 'Anthropic',
    mediaTypes: ['text', 'image'],
  },
  // Claude Sonnet 5 — current Sonnet tier, near-Opus quality on coding/agentic
  // work. $3/$15 per MTok, same as Sonnet 4.6 → 5 cr/generation.
  'claude-sonnet-5': {
    id: 'claude-sonnet-5',
    name: 'Claude Sonnet 5',
    provider: 'Anthropic',
    mediaTypes: ['text', 'image'],
  },

  // Google Gemini Text models (chat / reasoning via Google AI direct)
  'gemini-3.5-flash': {
    id: 'gemini-3.5-flash',
    name: 'Gemini 3.5 Flash',
    provider: 'Google',
    mediaTypes: ['text', 'image'],
  },
  'gemini-3.1-pro-preview': {
    id: 'gemini-3.1-pro-preview',
    name: 'Gemini 3.1 Pro (Preview)',
    provider: 'Google',
    mediaTypes: ['text', 'image'],
  },
  'gemini-3-flash-preview': {
    id: 'gemini-3-flash-preview',
    name: 'Gemini 3 Flash (Preview)',
    provider: 'Google',
    mediaTypes: ['text', 'image'],
  },
  // Deprecated by Google on 2026-05-07, shutdown 2027-05-07, replacement
  // gemini-3.5-flash-lite — https://ai.google.dev/gemini-api/docs/deprecations
  // Removed from the dropdown but NOT aliased: it still answers until shutdown,
  // and 3.5 Flash-Lite costs 2 cr against this one's 1 cr, so swapping the id
  // would change what a saved flow bills. Retire the entry after 2027-05-07.
  'gemini-3.1-flash-lite': {
    id: 'gemini-3.1-flash-lite',
    name: 'Gemini 3.1 Flash-Lite (deprecated → 3.5 Flash-Lite)',
    provider: 'Google',
    mediaTypes: ['text', 'image'],
  },
  // Gemini 3.6 Flash + 3.5 Flash-Lite — both GA 2026-07-21.
  'gemini-3.6-flash': {
    id: 'gemini-3.6-flash',
    name: 'Gemini 3.6 Flash',
    provider: 'Google',
    mediaTypes: ['text', 'image'],
  },
  'gemini-3.5-flash-lite': {
    id: 'gemini-3.5-flash-lite',
    name: 'Gemini 3.5 Flash-Lite',
    provider: 'Google',
    mediaTypes: ['text', 'image'],
  },
};

/**
 * Get model registry entry by ID
 */
export function getModelRegistryEntry(modelId: string): ModelRegistryEntry | undefined {
  return MODEL_REGISTRY[modelId];
}

/**
 * Get all video models from the registry
 * Used by video node model selectors
 */
export function getVideoModels(): ModelRegistryEntry[] {
  return Object.values(MODEL_REGISTRY).filter(
    (model) => model && model.mediaTypes && model.mediaTypes.includes('video')
  );
}

// ============================================================================
// Prompt Character Limits
// Maximum number of characters accepted by each model's prompt field.
// undefined = no enforced hard limit (large context-window text models).
// These are used by the UI to warn or truncate before sending.
// ============================================================================

export const MODEL_PROMPT_CHAR_LIMITS: Record<string, number> = {

  // ── Text models ─────────────────────────────────────────────────────────────
  // ⚠ UNIT WARNING: every value in this text block is a TOKEN count copied from
  // the model's context window, not a character count. At ~4 chars/token they
  // are roughly 4× too small to be used as character limits. No caller reads
  // them today (only the image/video/audio handlers call getPromptCharLimit),
  // and none should until these are either converted to characters or moved to
  // a separate token-limit table.
  // OpenAI GPT-5 family — 128 k-token context window (~4 chars/token)
  'gpt-5-nano':            128_000,
  'gpt-5-mini':            128_000,
  'gpt-5':                 128_000,
  'gpt-5.2':               128_000,
  'gpt-5.4-nano':          128_000,
  'gpt-5.4':               128_000,
  'gpt-5.4-pro':           128_000,
  // GPT-5.5 series (April 2026) — 256 k-token context window
  'gpt-5.5':               256_000,
  'gpt-5.5-pro':           256_000,
  'gpt-5.4-mini':          400_000,
  // GPT-5.6 series (July 2026) — 1.05 M-token context window
  'gpt-5.6-sol':         1_050_000,
  'gpt-5.6-terra':       1_050_000,
  'gpt-5.6-luna':        1_050_000,
  'gpt-4o-mini':           128_000,
  // Gemini 2.5 Flash — 1 M-token context window
  'gemini-2.5-flash':      500_000,
  // Moonshot AI Kimi via Ollama Cloud — 256 k-token context window for K2.6
  'kimi-k2.6':             256_000,
  'kimi-k2.5':             128_000,
  'kimi-k2-instruct':      128_000,
  // Alibaba Qwen via Ollama Cloud — 256 k-token context window for Qwen3.5/3-VL
  'qwen-3.5':              256_000,
  'qwen-3-vl-235b':        256_000,
  'qwen-3-coder-next':     256_000,
  // Legacy Qwen aliases (kept for saved flows)
  'qwen-3-235b':            32_000,
  'qwen-3.5-122b':          32_000,
  'qwen-3-next-80b':        32_000,
  'qwen-2.5-72b':           32_000,
  'qwen-2.5-7b':            32_000,
  // MiniMax via Ollama Cloud — 200 k context window for M2.7
  'minimax-m2.7':          200_000,
  'minimax-m2':            200_000,
  'minimax-m2.1':          1_000_000,
  'minimax-m2.5':          1_000_000,
  // Anthropic Claude
  // Legacy models — 200 k-token context window
  'claude-haiku-3-5':      200_000,
  'claude-sonnet-4':       200_000,
  // Current generation (Sonnet 4.5+, Haiku 4.5+, Opus 4.6+) — 1 M-token context window
  // per https://docs.anthropic.com/en/docs/about-claude/models
  'claude-sonnet-4-5':   1_000_000,
  'claude-haiku-4-5':    1_000_000,
  'claude-sonnet-4-6':   1_000_000,
  'claude-opus-4-6':     1_000_000,
  'claude-opus-4-7':     1_000_000,
  'claude-opus-4-8':     1_000_000,
  'claude-opus-5':       1_000_000,
  'claude-sonnet-5':     1_000_000,
  // Google Gemini Text models — 1M+ token context windows
  'gemini-3.5-flash':      1_000_000,
  'gemini-3.1-pro-preview': 1_000_000,
  'gemini-3-flash-preview': 1_000_000,
  'gemini-3.1-flash-lite': 1_000_000,
  'gemini-3.6-flash':      1_000_000,
  'gemini-3.5-flash-lite': 1_000_000,
  // xAI Grok text — context windows per GET /v1/models (verified 2026-08-08).
  // 4.3 and the 4.20 family are 1 M; 4.5 is 500 k; Build is 256 k.
  'grok-4.5':                500_000,
  'grok-4.3':              1_000_000,
  'grok-build-0.1':          256_000,

  // ── Image models ─────────────────────────────────────────────────────────────
  // Google Gemini image generation
  'gemini-2.5-flash-image':     2_048,
  'gemini-nano-banana':         2_048,
  'gemini-3-pro-image-preview': 2_048,
  'gemini-3.1-flash-image':     2_048,
  'gemini-3.1-flash-lite-image': 2_048,
  // OpenAI image generation (Responses API — flexible, but practical limit)
  'gpt-image-1':               32_000,
  'gpt-image-1.5':             32_000,
  'gpt-image-2':               32_000,
  'gpt-image-1-mini':          32_000,
  // DALL-E (hard limits per OpenAI API docs)
  'dall-e-3':                   4_000,
  'dall-e-2':                   1_000,
  // FLUX — T5-XXL encoder (~512 tokens ≈ 2 048 chars)
  'flux-2-max':                 2_048,
  'flux-2-pro':                 2_048,
  'flux-2-turbo':               2_048,
  'flux-2-flash':               2_048,
  'flux-pro-fill':              2_048,
  'flux-1-kontext-pro':         2_048,
  'flux-1-kontext-max':         2_048,
  // fal.ai image models
  'z-image-turbo':              1_024,
  'qwen-image':                 2_048,
  'qwen-image-edit':            2_048,
  'seedream-4.5':               2_048,
  'recraft-v3':                 1_000,
  'recraft-v4':                 1_000,
  'recraft-v4-pro':             1_000,
  'qwen-image-2':                 800,  // Max 800 chars per fal.ai docs
  'qwen-image-2-pro':             800,  // Same encoder limit as v2 standard
  'seedream-5-lite':            2_048,
  'glm-image':                  2_048,
  'kling-image-o3':             2_500,  // Verified: fal.ai Kling Image schema says max 2500 chars
  // Google Imagen 4 family — 480 tokens ≈ 2 048 chars per Google docs
  // xAI Grok image. xAI's own docs publish no prompt cap for
  // /v1/images/generations, but our proxy rejects anything longer with a 400
  // before it ever reaches xAI (server/routes/xai.ts:83 — "Prompt exceeds
  // maximum length of 1024 characters"), so 1 024 is the real enforced ceiling.
  'grok-image':                 1_024,
  'grok-image-pro':             1_024,
  'grok-imagine-image-quality': 1_024,
  // Grok Image 2.0: xAI max_prompt_length is 64 000, and the proxy lifts its
  // 1 024 cap for this model only (server/config/grokImage.ts).
  'grok-imagine-image-2.0':    64_000,

  // ── Audio models (ElevenLabs) ────────────────────────────────────────────────
  // True per-request character caps from the ElevenLabs model table
  // (https://elevenlabs.io/docs/overview/models). Exceeding them is a hard
  // provider rejection, not a quality degradation.
  'eleven_v3':                  5_000,
  'eleven_flash_v2_5':         40_000,
  'eleven_turbo_v2_5':         40_000,
  'eleven_multilingual_v2':    10_000,
  // No entry for the Sound Effects model or Eleven Music (`music_v1`):
  // ElevenLabs publishes no character cap for either endpoint, and guessing one
  // would block valid prompts. Left out deliberately — the check no-ops.

  // ── Avatar / talking-head video (HeyGen via fal.ai) ──────────────────────────
  // "Avatar scripts: Maximum 5,000 characters"
  // (https://developers.heygen.com/docs/usage-limits); the fal.ai schema for
  // fal-ai/heygen/avatar3/digital-twin repeats it as
  // "The text to be converted to speech (max 5000 characters)".
  // Translate / lipsync / photo-video take no script, so they carry no entry.
  'heygen-digital-twin-v3':     5_000,
  'heygen-digital-twin-v4':     5_000,
  'heygen-digital-twin-v5':     5_000,
  'heygen-video-agent':         5_000,

  // ── Video models ─────────────────────────────────────────────────────────────
  // Google VEO (all variants) — 1 024-char prompt limit
  'veo-3.1-fast-generate-preview':  1_024,
  'veo-3.1-generate-preview':       1_024,
  'veo-3.1-lite-generate-preview':  1_024,
  'veo-3-fast':                     1_024,
  'veo-3-pro':                      1_024,
  'veo-3':                          1_024,
  // Kling (via fal.ai)
  'kling-2.6':                     2_500,
  'kling-2.6-audio':               2_500,
  'kling-2.5-turbo':               2_500,
  'kling-3.0-standard':            2_500,
  'kling-3.0-standard-audio':      2_500,
  'kling-3.0-pro':                 2_500,
  'kling-3.0-pro-audio':           2_500,
  'kling-avatar':                  2_500,
  // Wan
  'wan-2.2':                       2_000,
  'wan-2.6':                       2_000,
  'wan-2.7':                       2_000,
  // LTX
  'ltx-2':                         2_000,
  'ltx-2-fast':                    2_000,
  'ltx-2.3':                       2_000,
  'ltx-2.3-fast':                  2_000,
  // Other fal.ai video models
  'pixverse-5.5':                  2_000,
  'pixverse-6':                    2_000,
  'hunyuan-1.5':                   2_000,
  'minimax-hailuo':                1_000,
  'hailuo-2.3':                    2_000,
  // MiniMax H3 — schema maxLength is 50 000, but fal's own prompting guide
  // quotes ~7 000 as the practical ceiling. Capped at 2 000 like its siblings.
  'minimax-h3':                    2_000,
  // xAI Grok Imagine Video — server caps at 2 000 chars (see xai.ts route)
  'grok-imagine-video':            2_000,
  'grok-imagine-video-1.5-preview': 2_000,
  // ByteDance Seedance (via fal.ai). Schema maxLength is far higher, but the
  // sibling video models all cap at 2 000 and long prompts degrade adherence.
  'seedance-2.5':                  2_000,
  'seedance-2':                    2_000,
  'seedance-2-fast':               2_000,
  'seedance-2-mini':               2_000,
};

/**
 * Get the maximum prompt character limit for a model.
 * Returns undefined if no limit is configured for the given model ID.
 */
export function getPromptCharLimit(modelId: string): number | undefined {
  return MODEL_PROMPT_CHAR_LIMITS[modelId];
}

/**
 * Human-readable name for a model, for UI copy (e.g. the prompt-limit modal).
 *
 * `MODEL_REGISTRY` is NOT a complete list of models — it is the list that populates
 * the model-selector dropdowns, and `getVideoModels()` derives dropdown options from
 * it. Over 40 model ids that have a prompt character limit have no registry entry
 * (`grok-imagine-video`, `wan-2.6`, `kling-2.6`, the whole gpt-5.x family, …), so
 * `MODEL_REGISTRY[id]?.name || id` showed users a raw id like `grok-imagine-video`.
 *
 * Adding registry entries for them would fix the copy but silently inject new options
 * into every model dropdown, so the fallback is formatting instead: split the id on
 * separators, upper-case known vendor/format tokens, and title-case the rest.
 */
const MODEL_NAME_TOKEN_OVERRIDES: Record<string, string> = {
  gpt: 'GPT',
  ltx: 'LTX',
  glm: 'GLM',
  veo: 'VEO',
  xai: 'xAI',
  ai: 'AI',
  hd: 'HD',
  sd: 'SD',
  tts: 'TTS',
  v2: 'v2',
  v3: 'v3',
  v4: 'v4',
  v5: 'v5',
};

/** Ids whose display name cannot be derived mechanically. Keep this list tiny. */
const MODEL_NAME_EXCEPTIONS: Record<string, string> = {
  'dall-e-2': 'DALL·E 2',
  'dall-e-3': 'DALL·E 3',
};

export function getModelDisplayName(modelId: string | null | undefined): string {
  if (!modelId) return 'this model';

  const registered = MODEL_REGISTRY[modelId];
  if (registered?.name) return registered.name;

  if (MODEL_NAME_EXCEPTIONS[modelId]) return MODEL_NAME_EXCEPTIONS[modelId];

  // `eleven_v3` → "Eleven v3"; `grok-imagine-video` → "Grok Imagine Video";
  // `wan-2.6` → "Wan 2.6". Dots are kept — they are version numbers.
  const tokens = modelId
    .split(/[-_\s]+/)
    .filter(Boolean)
    .map((token) => {
      const lower = token.toLowerCase();
      if (MODEL_NAME_TOKEN_OVERRIDES[lower]) return MODEL_NAME_TOKEN_OVERRIDES[lower];
      // Leave version-ish tokens (2.5, 4o, 3b) exactly as they are.
      if (/^\d/.test(token)) return token;
      return token.charAt(0).toUpperCase() + token.slice(1);
    })
    // ElevenLabs writes decimals with the separator (`eleven_flash_v2_5`), which the
    // split above turns into two tokens. Rejoin them: "v2" + "5" → "v2.5".
    .reduce<string[]>((acc, token) => {
      const previous = acc[acc.length - 1];
      if (previous && /^v\d+$/.test(previous) && /^\d+$/.test(token)) {
        acc[acc.length - 1] = `${previous}.${token}`;
        return acc;
      }
      acc.push(token);
      return acc;
    }, []);

  return tokens.join(' ').trim();
}
